import { groupBy, size } from 'lodash';

import { GetHypotheticalTransfers_HypotheticalCltFragment } from '../graphql/GetHypotheticalTransfers.generated';
import {
  GetHypotheticalTransfers_EstateWaterfallTransferFragment,
  GetHypotheticalTransfers_HypotheticalCrtFragment,
  GetHypotheticalTransfers_HypotheticalInsuranceFragment,
} from '../graphql/GetHypotheticalTransfers.generated';
import { GetHypotheticalTransfers_HypotheticalGratFragment } from '../graphql/GetHypotheticalTransfers.generated';
import {
  SaleLoanWithProjections,
  TransferWithValue,
} from './useGetHypotheticalTransfers';

// Inbound and outbound transfers are in relation to the transfer reference obj
interface TransfersCardHypotheticalTransfersInput {
  // Basic transfers
  inboundTransfers: TransferWithValue[];
  outboundTransfers: TransferWithValue[];

  // Loans
  sourceLoans: SaleLoanWithProjections[];
  recipientLoans: SaleLoanWithProjections[];

  // GRATs
  sourceHypotheticalGrats: GetHypotheticalTransfers_HypotheticalGratFragment[];
  recipientHypotheticalGrats: GetHypotheticalTransfers_HypotheticalGratFragment[];

  // GRAT Annuities
  inboundGratAnnuities: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundGratAnnuities: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];

  // GRAT Beneficiary Payments
  inboundGratBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundGratBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];

  // QPRT Beneficiary Payments
  inboundQprtBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundQprtBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];

  // CRTs
  sourceHypotheticalCrts: GetHypotheticalTransfers_HypotheticalCrtFragment[];
  recipientHypotheticalCrts: GetHypotheticalTransfers_HypotheticalCrtFragment[];

  // CRT Income Payments
  inboundHypotheticalCrtIncomePayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundHypotheticalCrtIncomePayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];

  // CRT Beneficiary Payments
  inboundHypotheticalCrtBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundHypotheticalCrtBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];

  // CLTs (Charitable Lead Trusts)
  sourceHypotheticalClts: GetHypotheticalTransfers_HypotheticalCltFragment[];
  recipientHypotheticalClts: GetHypotheticalTransfers_HypotheticalCltFragment[];

  // CLT Income Payments
  inboundHypotheticalCltIncomePayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundHypotheticalCltIncomePayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];

  // CLT Beneficiary Payments
  inboundHypotheticalCltBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundHypotheticalCltBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];

  // Insurance
  sourceHypotheticalInsurance: GetHypotheticalTransfers_HypotheticalInsuranceFragment[];
  recipientHypotheticalInsurance: GetHypotheticalTransfers_HypotheticalInsuranceFragment[];
}

export function useTransfersCardHypotheticalTransfers(
  props: TransfersCardHypotheticalTransfersInput
) {
  const {
    // Basic transfers
    inboundTransfers,
    outboundTransfers,

    // Loans
    sourceLoans,
    recipientLoans,

    // GRATs
    recipientHypotheticalGrats,
    sourceHypotheticalGrats,

    // GRAT Annuities
    inboundGratAnnuities,
    outboundGratAnnuities,

    // GRAT Beneficiary Payments
    inboundGratBeneficiaryPayments,
    outboundGratBeneficiaryPayments,

    // QPRT Beneficiary Payments
    inboundQprtBeneficiaryPayments,
    outboundQprtBeneficiaryPayments,

    // CRTs
    sourceHypotheticalCrts,
    recipientHypotheticalCrts,

    // CRT Income Payments
    inboundHypotheticalCrtIncomePayments,
    outboundHypotheticalCrtIncomePayments,

    // CRT Beneficiary Payments
    inboundHypotheticalCrtBeneficiaryPayments,
    outboundHypotheticalCrtBeneficiaryPayments,

    // CLTs
    sourceHypotheticalClts,
    recipientHypotheticalClts,

    // CLT Income Payments
    inboundHypotheticalCltIncomePayments,
    outboundHypotheticalCltIncomePayments,

    // CLT Beneficiary Payments
    inboundHypotheticalCltBeneficiaryPayments,
    outboundHypotheticalCltBeneficiaryPayments,

    // Insurance
    sourceHypotheticalInsurance,
    recipientHypotheticalInsurance,
  } = props;

  // Group related entities for easier counting and checking
  const transfers = {
    inboundTransfers: inboundTransfers ?? [],
    outboundTransfers: outboundTransfers ?? [],
  };

  const saleLoans = {
    recipientLoans: recipientLoans ?? [],
    sourceLoans: sourceLoans ?? [],
  };

  const hypotheticalGrats = {
    recipientGrats: recipientHypotheticalGrats ?? [],
    sourceGrats: sourceHypotheticalGrats ?? [],
  };

  const hypotheticalCrts = {
    recipientCrts: recipientHypotheticalCrts ?? [],
    sourceCrts: sourceHypotheticalCrts ?? [],
  };

  const hypotheticalClts = {
    recipientClts: recipientHypotheticalClts ?? [],
    sourceClts: sourceHypotheticalClts ?? [],
  };

  const hypotheticalInsurance = {
    recipientInsurance: recipientHypotheticalInsurance ?? [],
    sourceInsurance: sourceHypotheticalInsurance ?? [],
  };

  // Basic flag checks
  const hasTransfers =
    transfers.inboundTransfers.length > 0 ||
    transfers.outboundTransfers.length > 0;

  const hasSaleLoans =
    saleLoans.recipientLoans.length > 0 || saleLoans.sourceLoans.length > 0;

  const hasHypotheticalGrats =
    hypotheticalGrats.recipientGrats.length > 0 ||
    hypotheticalGrats.sourceGrats.length > 0;

  const hasHypotheticalCrts =
    hypotheticalCrts.recipientCrts.length > 0 ||
    hypotheticalCrts.sourceCrts.length > 0;

  const hasHypotheticalClts =
    hypotheticalClts.recipientClts.length > 0 ||
    hypotheticalClts.sourceClts.length > 0;

  const hasHypotheticalInsurance =
    hypotheticalInsurance.recipientInsurance.length > 0 ||
    hypotheticalInsurance.sourceInsurance.length > 0;

  // GRAT Annuities checks
  const hasGratAnnuities =
    inboundGratAnnuities.length > 0 || outboundGratAnnuities.length > 0;

  const hasInboundGratAnnuities = inboundGratAnnuities.length > 0;

  const hasOutboundGratAnnuities = outboundGratAnnuities.length > 0;

  // GRAT Beneficiary Payments checks
  const hasGratBeneficiaryPayments =
    inboundGratBeneficiaryPayments.length > 0 ||
    outboundGratBeneficiaryPayments.length > 0;

  const hasInboundGratBeneficiaryPayments =
    inboundGratBeneficiaryPayments.length > 0;

  const hasOutboundGratBeneficiaryPayments =
    outboundGratBeneficiaryPayments.length > 0;

  // QPRT Beneficiary Payments checks
  const hasQprtBeneficiaryPayments =
    inboundQprtBeneficiaryPayments.length > 0 ||
    outboundQprtBeneficiaryPayments.length > 0;

  const hasInboundQprtBeneficiaryPayments =
    inboundQprtBeneficiaryPayments.length > 0;

  const hasOutboundQprtBeneficiaryPayments =
    outboundQprtBeneficiaryPayments.length > 0;

  const hasHypotheticalCrtIncomePayments =
    inboundHypotheticalCrtIncomePayments.length > 0 ||
    outboundHypotheticalCrtIncomePayments.length > 0;

  const numInboundHypotheticalCrtIncomePayments = Object.keys(
    groupBy(
      inboundHypotheticalCrtIncomePayments,
      (crtIncomePayment) => crtIncomePayment.destination?.node?.id
    )
  ).length;

  const numOutboundHypotheticalCrtIncomePayments = Object.keys(
    groupBy(
      outboundHypotheticalCrtIncomePayments,
      (crtIncomePayment) => crtIncomePayment.destination?.node?.id
    )
  ).length;

  const hasHypotheticalCrtBeneficiaryPayments =
    inboundHypotheticalCrtBeneficiaryPayments.length > 0 ||
    outboundHypotheticalCrtBeneficiaryPayments.length > 0;

  const hasInboundHypotheticalCrtBeneficiaryPayments =
    inboundHypotheticalCrtBeneficiaryPayments.length > 0;

  const hasOutboundHypotheticalCrtBeneficiaryPayments =
    outboundHypotheticalCrtBeneficiaryPayments.length > 0;

  const hasHypotheticalCltIncomePayments =
    inboundHypotheticalCltIncomePayments.length > 0 ||
    outboundHypotheticalCltIncomePayments.length > 0;

  const numInboundHypotheticalCltIncomePayments = Object.keys(
    groupBy(
      inboundHypotheticalCltIncomePayments,
      (cltIncomePayment) => cltIncomePayment.destination?.node?.id
    )
  ).length;

  const numOutboundHypotheticalCltIncomePayments = Object.keys(
    groupBy(
      outboundHypotheticalCltIncomePayments,
      (cltIncomePayment) => cltIncomePayment.destination?.node?.id
    )
  ).length;

  const hasHypotheticalCltBeneficiaryPayments =
    inboundHypotheticalCltBeneficiaryPayments.length > 0 ||
    outboundHypotheticalCltBeneficiaryPayments.length > 0;

  const hasInboundHypotheticalCltBeneficiaryPayments =
    inboundHypotheticalCltBeneficiaryPayments.length > 0;

  const hasOutboundHypotheticalCltBeneficiaryPayments =
    outboundHypotheticalCltBeneficiaryPayments.length > 0;

  // Sales and loans count as both inbound and outbound because
  // there's an inbound component and an outbound component for each
  const totalAssociatedSaleLoans =
    size(saleLoans.sourceLoans) + size(saleLoans.recipientLoans);

  // Calculate total counts for outbound transfers
  const outboundCount =
    (outboundTransfers?.length ?? 0) +
    totalAssociatedSaleLoans +
    size(hypotheticalGrats.sourceGrats) +
    size(hypotheticalCrts.sourceCrts) +
    size(hypotheticalClts.sourceClts) +
    size(hypotheticalInsurance.sourceInsurance) +
    (hasOutboundGratAnnuities ? 1 : 0) +
    (hasOutboundGratBeneficiaryPayments ? 1 : 0) +
    (hasOutboundQprtBeneficiaryPayments ? 1 : 0) +
    numOutboundHypotheticalCrtIncomePayments +
    (hasOutboundHypotheticalCrtBeneficiaryPayments ? 1 : 0) +
    numOutboundHypotheticalCltIncomePayments +
    (hasOutboundHypotheticalCltBeneficiaryPayments ? 1 : 0);

  // Calculate total counts for inbound transfers
  const inboundCount =
    (inboundTransfers?.length ?? 0) +
    totalAssociatedSaleLoans +
    size(hypotheticalGrats.recipientGrats) +
    size(hypotheticalCrts.recipientCrts) +
    size(hypotheticalClts.recipientClts) +
    size(hypotheticalInsurance.recipientInsurance) +
    (hasInboundGratAnnuities ? 1 : 0) +
    (hasInboundGratBeneficiaryPayments ? 1 : 0) +
    (hasInboundQprtBeneficiaryPayments ? 1 : 0) +
    numInboundHypotheticalCrtIncomePayments +
    (hasInboundHypotheticalCrtBeneficiaryPayments ? 1 : 0) +
    numInboundHypotheticalCltIncomePayments +
    (hasInboundHypotheticalCltBeneficiaryPayments ? 1 : 0);

  return {
    outboundCount,
    inboundCount,
    hasTransfers,
    hasSaleLoans,
    hasHypotheticalGrats,
    hasHypotheticalCrts,
    hasHypotheticalClts,
    hasHypotheticalInsurance,
    hasGratAnnuities,
    hasInboundGratAnnuities,
    hasOutboundGratAnnuities,
    hasGratBeneficiaryPayments,
    hasInboundGratBeneficiaryPayments,
    hasOutboundGratBeneficiaryPayments,
    hasQprtBeneficiaryPayments,
    hasInboundQprtBeneficiaryPayments,
    hasOutboundQprtBeneficiaryPayments,
    hasHypotheticalCrtIncomePayments,
    hasHypotheticalCrtBeneficiaryPayments,
    hasInboundHypotheticalCrtBeneficiaryPayments,
    hasOutboundHypotheticalCrtBeneficiaryPayments,
    hasHypotheticalCltIncomePayments,
    hasHypotheticalCltBeneficiaryPayments,
    hasInboundHypotheticalCltBeneficiaryPayments,
    hasOutboundHypotheticalCltBeneficiaryPayments,
    hypotheticalGrats,
    hypotheticalCrts,
    hypotheticalClts,
    hypotheticalInsurance,
    saleLoans,
    transfers,
  };
}
